import { InterfaceLinkDtoSchema } from '@/schemas/chip';
import { Rotation } from '@/models/data/Rotation';
import { z } from 'zod';
import { TileType } from '@/models/TileTypes';
import { SideSchema, PointDataSchema, ChipIdSchema } from '@/schemas/common';
import { TileMetaSchema } from '@/schemas/tiles';

const TileTypeSchema = z.nativeEnum(TileType);

const ClipboardConnectionPointSchema = z.object({
  x: z.number().int(),
  y: z.number().int(),
  side: SideSchema,
});

const ClipboardTileEntrySchema = z.object({
  sourceTileId: z.string().min(1).max(256).optional(),
  tileType: TileTypeSchema,
  relativeLocation: PointDataSchema,
  // Clipboard chip references are local indexes, never persisted instance IDs.
  meta: TileMetaSchema.safeExtend({
    partOfChip: z.union([
      TileMetaSchema.shape.partOfChip,
      z.string().regex(/^__clip_chip_\d+$/),
    ]),
  }),
  state: z.unknown().optional(),
  interfaceLink: InterfaceLinkDtoSchema.optional(),
});

const ClipboardConnectionSchema = z.object({
  a: ClipboardConnectionPointSchema,
  b: ClipboardConnectionPointSchema,
});

const ClipboardEdgeHintSchema = z.object({
  relativeLocation: PointDataSchema,
  side: SideSchema,
});

const ClipboardChipInstanceSchema = z.object({
  bundleWidth: z.number().int().positive().max(4096).optional(),
  rotation: z.nativeEnum(Rotation).optional(),
  chipId: ChipIdSchema,
  relativeOrigin: PointDataSchema,
  localIndex: z.number().int().nonnegative(),
});

export const ClipboardDataSchema = z.object({
  version: z.literal(1),
  operation: z.enum(['copy', 'cut']).optional(),
  origin: z.object({ x: z.number().int(), y: z.number().int() }).optional(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  tiles: z.array(ClipboardTileEntrySchema),
  connections: z.array(ClipboardConnectionSchema),
  disconnected: z.array(ClipboardConnectionSchema).optional(),
  edgeHints: z.array(ClipboardEdgeHintSchema),
  chipInstances: z.array(ClipboardChipInstanceSchema),
});

export type ClipboardDataDto = z.infer<typeof ClipboardDataSchema>;
